<template>
  <div :class="['mms-wrapper mms-widget', isMmsOpen ? 'opened' : 'closed']">
    <loading-icon v-if="isLoading"></loading-icon>
    <div v-show="!isLoading && isMmsOpen" class="mms-section only-one-mms">
      <div :class="['mms-upload-section', { ['has-error']: mediaError }]">
        <small v-if="mediaError" class="error-label">{{mediaError}}</small>
        <upload-attachment
            v-else-if="!currentMms"
            ref="imageUploader"
            :errorDisplayImage="errorMessage"
            extraClasses="mms-attachment-label"
            label="mms"
            loadingClasses="loading-mms"
            :ownerId="messageId"
            :ownerType="messageType"
            showInputFile
            showPreviewModal
            :uploadConfig="uploadConfig"
            @adviseLoading="adviseLoading"
            @close="closeModal"
            @setImageLink="setImageLink"
            @showPreviewModal="setPreviewModal">
        </upload-attachment>

        <div v-else class="mms-preview-section">
          <div v-if="currentMms && mmsAsLink" class="image-as-link-preview">
            <svg viewBox="0 0 512.092 512.092" class="sb-icon">
              <use xlink:href="#link-icon"></use>
            </svg>
          </div>
          <image-preview
              v-if="currentMms"
              extraClasses="thumbnail-label mms-image"
              deleteText="remove"
              :key="currentMms.id"
              :src="currentMms.thumb"
              :uuid="currentMms.uuid"
              @adviseLoading="adviseLoading"
              @reload="getAttachedMedia">
          </image-preview>
        </div>
        <sms-action-footer
            v-if="!isBackup"
            @closeAction="$emit('closeMms', 'mms', true)">
        </sms-action-footer>
      </div>
      <stored-images-container
          v-if="!isBackup"
          ref="storedImages"
          :currentMms="currentMms"
          :messageType="messageType"
          :messageId="messageId"
          :messageModuleName="messageModuleName"
          @adviseLoading="adviseLoading"
          @getAttachedMedia="getAttachedMedia"
          @previewImage="setPreviewModal">
      </stored-images-container>
    </div>
    <image-preview-modal
        v-if="showModal"
        :alt="previewFile.file_name"
        :disableAction="disableAction"
        :imageSrc="previewFile.url"
        :isLink="isLink"
        :isBackup="isBackup"
        expandMMS
        :modalConfig="modalConfig"
        @errorLoadingImage="errorLoadingImage"
        @upload="uploadImage"
        @close="closeModal">
    </image-preview-modal>
  </div>
</template>

<script>
  import AxiosMixin from '../../mixins/AxiosMixin'
  import ImagePreview from '../shared/ImagePreview'
  import ImagePreviewModal from '../shared/ImagePreviewModal'
  import LoadingIcon from '../shared/LoadingIcon'
  import SmsActionFooter from '../Sms/SmsActionFooter'
  import StoredImagesContainer from './StoredImagesContainer'
  import UploadAttachment from '../FormItems/UploadAttachment'
  import { mapState } from 'vuex'

  export default {
    props: {
      isMmsOpen: Boolean,
      isSmsBackup: Boolean,
      mediaError: String,
      messageModuleName: String,
    },

    name: 'Mms',

    components: {
      ImagePreview,
      LoadingIcon,
      ImagePreviewModal,
      SmsActionFooter,
      StoredImagesContainer,
      UploadAttachment,
    },

    mixins: [AxiosMixin],

    data() {
      return {
        isLoading: false,
        disableAction: false,
        errorMessage: null,
        modalConfig: {
          actionButton: "include this image",
          showClose: true,
          title: 'include this image?',
          wrapperClasses: 'image-preview-modal'
        },
        previewFile: null,
        sendAsLink: false,
        showModal: false,
        uploadConfig: {
          differentLabel: 'add',
          differentLabelBelow: 'photo',
          displayType: `mms-${this.messageId}`,
          uploadIcon: 'camera-icon',
          uploadIconViewbox: '0 0 100 90',
        },
      }
    },

    methods: {
      adviseLoading(isLoading) {
        this.isLoading = isLoading
      },

      closeModal(isCancel, isStoredImage) {
        this.showModal = false
        this.$refs.imageUploader?.clearFile()
        if (isCancel) {
          this.resetSendAsLink()
          !isStoredImage && this.updateImageLabel('mms_cancelled')
        }
        this.previewFile = null
      },

      extraConfigForImageModal(isStoredImage) {
        return {
          ...this.modalConfig,
          isStoredImage: isStoredImage
        }
      },

      configureAndShowModal(isStoredImage) {
        this.modalConfig = this.extraConfigForImageModal(isStoredImage)
        this.showModal = true
      },

      errorLoadingImage(errorMessage) {
        this.errorMessage = errorMessage
        this.updateImageLabel('mms_resize_fail')
      },

      errorUpdateImageLabel() {
        this.errorMessage = 'error updating image, please try again'
      },

      finishGettingAttachedMedia() {
        this.isLoading = false
      },

      getAttachedMedia() {
        const dataToUpdate = { image_type: this.sendAsLink ? 'link' : 'mms' }
        this.isLoading = true
        this.$emit('updateCampaign', dataToUpdate)
        this.$emit('getAttachedMedia')
      },

      resetSendAsLink() {
        this.sendAsLink = this.mmsAsLink
      },

      setImageLink(file, isStoredImage) {
        this.isLoading = false
        this.disableAction = false
        this.previewFile = file
        this.previewFile.url = file.resized_url
        //this.uploadImage(false, isStoredImage);
        this.configureAndShowModal()
      },

      setPreviewModal(file, isStoredImage) {
        this.errorMessage = null
        this.previewFile = file
        this.configureAndShowModal(isStoredImage)
      },

      uploadImage(isLink, isStoredImage) {
        this.sendAsLink = isLink
        isStoredImage ? this.$refs.storedImages.processClick(this.previewFile, 'upload') : this.getAttachedMedia()
        this.closeModal()
      },

      updateImageLabel(imageLabel) {
        const url = `/api/v3/attachments/${this.previewFile.uuid}`,
              methodType = 'put',
              params = { label: imageLabel },
              successHandler = () => {
                this.getAttachedMedia()
              }
        this.axiosMethod({methodType, url, params, onSuccess: successHandler, onError: this.errorUpdateImageLabel})
      },

      updateUploadConfig() {
        this.$set(this.uploadConfig, 'displayType', `mms-${this.messageId}`)
        if (this.isBackup) {
          this.$delete(this.uploadConfig, 'differentLabelBelow')
          this.$set(this.uploadConfig, 'differentLabel', 'upload backup photo')
        }
      }
    },

    created() {
      this.updateUploadConfig()
      this.resetSendAsLink()
    },

    computed: {
      currentMms: function () {
        return this.mmsMedia[0] || null
      },

      isLink: function() {
        const isLink = this.isBackup ? this.isSmsBackup : this.sendAsLink
        return isLink
      },

      ...mapState({
        isBackup (state) { return state[this.messageModuleName]?.isBackup },
        messageId (state) { return state[this.messageModuleName]?.messageId },
        messageType (state) { return state[this.messageModuleName]?.messageType },
        mmsAsLink (state, getters) { return getters[`${this.messageModuleName}/mmsAsLink`] },
        mmsMedia (state, getters) { return getters[`${this.messageModuleName}/mms`] },
      }, [
        'maxMmsImageSize',
        'maxMmsImageResizeWidth'
      ])
    },

    watch: {
      isLoading: function () {
        this.$emit('setMediaLoading', 'mms', this.isLoading)
      },

      isSmsBackup: function() {
        this.updateUploadConfig()
      },

      mmsAsLink: function() {
        this.resetSendAsLink()
      }
    },
  }
</script>
